import { snackController } from '@/components/snack-bar/snack-bar-controller'
import { Zero } from '@/constants'
import { bnHelper } from '@/helpers/bignumber-helper'
import { priceHelper } from '@/helpers/pancakePriceHelper'
import { PoolInfo, StakingEventHandler } from '@/helpers/stakingEventHandler/staking-event-handler'
import { walletStore } from '@/stores/wallet-store'
import { FixedNumber } from '@ethersproject/bignumber'
import { action, computed, IReactionDisposer, observable, reaction, runInAction, when } from 'mobx'
import { asyncAction } from 'mobx-utils'
import moment from 'moment'
import { Subject, timer } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

export class SbStakingViewModel {
  _disposers: IReactionDisposer[] = []
  private _unsubcrible = new Subject()
  //SB
  @observable sbStakingPool = 0
  @observable sbStakedAmount = Zero
  @observable openSBStakingDialog = false
  @observable openSBUnStakeDialog = false
  @observable openSBHavestDialog = false
  @observable sbStakeInputAmount?: string
  @observable sbStakingAPR = Zero
  @observable stakeLoading = false
  @observable sbApproved = false
  @observable sbApproving = false
  @observable sbPrice = '0'
  @observable sbPendingReward = Zero
  @observable sbPoolInfo?: PoolInfo
  @observable sbUnStakingAmount = Zero
  @observable sbRequestWithdrawTime?: any

  //STE
  @observable stePrice = '0'
  @observable steStakingPool = 0
  @observable steStakedAmount = Zero
  @observable openSTEStakingDialog = false
  @observable openSTEUnStakeDialog = false
  @observable openSTEHavestDialog = false
  @observable steStakeInputAmount?: string
  @observable steStakingAPR = Zero
  @observable stakeSTELoading = false
  @observable steApproved = false
  @observable steApproving = false
  @observable stePendingReward = Zero
  @observable stePoolInfo?: PoolInfo
  @observable steUnStakingAmount = Zero
  @observable steRequestWithdrawTime?: any

  //USDT
  @observable usdtPrice = '0'
  @observable usdtStakingPool = 0
  @observable usdtStakedAmount = Zero
  @observable openUSDTStakingDialog = false
  @observable openUSDTUnStakeDialog = false
  @observable openUSDTHavestDialog = false
  @observable usdtStakeInputAmount?: string
  @observable usdtStakingAPR = Zero
  @observable stakeUSDTLoading = false
  @observable usdtApproved = false
  @observable usdtApproving = false
  @observable usdtPendingReward = Zero
  @observable usdtPoolInfo?: PoolInfo
  @observable usdtUnStakingAmount = Zero
  @observable usdtRequestWithdrawTime?: any

  stakingSBPoolHandler?: StakingEventHandler
  stakingSTEPoolHandler?: StakingEventHandler
  stakingUSDTPoolHandler?: StakingEventHandler
  constructor() {
    this.stakingSBPoolHandler = new StakingEventHandler('SB')
    this.stakingSTEPoolHandler = new StakingEventHandler('STE')
    this.stakingUSDTPoolHandler = new StakingEventHandler('USDT')
    this.loadData()

    this._disposers = [
      reaction(
        () => walletStore.account,
        () => {
          if (walletStore.isChainIdValid) {
            if (walletStore.web3) {
              this.stakingSBPoolHandler?.injectMetamask(walletStore.web3)
              this.stakingSTEPoolHandler?.injectMetamask(walletStore.web3)
              this.stakingUSDTPoolHandler?.injectMetamask(walletStore.web3)
            }
          }
        }
      )
    ]

    timer(1000, 15000)
      .pipe(takeUntil(this._unsubcrible))
      .subscribe(async () => {
        if (this.stakingSBPoolHandler) this.sbStakingAPR = await this.stakingSBPoolHandler.getStakeApy()
        if (this.stakingSTEPoolHandler) this.steStakingAPR = await this.stakingSTEPoolHandler.getStakeApy()
        if (this.stakingUSDTPoolHandler) this.usdtStakingAPR = await this.stakingUSDTPoolHandler.getStakeApy()
      })

    this._disposers.push(
      when(
        () => walletStore.connected,
        async () => {
          if (walletStore.chainId == process.env.VUE_APP_CHAIN_ID) {
            if (walletStore.web3) {
              this.stakingSBPoolHandler?.injectMetamask(walletStore.web3)
              this.stakingSTEPoolHandler?.injectMetamask(walletStore.web3)
              this.stakingUSDTPoolHandler?.injectMetamask(walletStore.web3)
            }
            this.stakingSBPoolHandler
              ?.approved(walletStore.account)
              .then(approved => runInAction(() => (this.sbApproved = approved)))
            this.stakingSTEPoolHandler
              ?.approved(walletStore.account)
              .then(approved => runInAction(() => (this.steApproved = approved)))
            this.stakingUSDTPoolHandler
              ?.approved(walletStore.account)
              .then(approved => runInAction(() => (this.usdtApproved = approved)))
            this.fetchUserInfo()
          }
        }
      )
    )
  }

  destroy() {
    this._unsubcrible.next()
    this._unsubcrible.complete()
    this._disposers.forEach(d => d())
  }

  @asyncAction *loadData() {
    if (this.stakingSBPoolHandler) yield this.stakingSBPoolHandler.load()
    this.sbPoolInfo = this.stakingSBPoolHandler?.poolInfo

    if (this.stakingSTEPoolHandler) yield this.stakingSTEPoolHandler.load()
    this.stePoolInfo = this.stakingSTEPoolHandler?.poolInfo

    if (this.stakingUSDTPoolHandler) yield this.stakingUSDTPoolHandler.load()
    this.usdtPoolInfo = this.stakingUSDTPoolHandler?.poolInfo

    priceHelper.getTokenPrice().then(p => {
      runInAction(() => {
        this.sbPrice = priceHelper.tokenPriceBUSD
        this.stePrice = priceHelper.tokenSTEPrice
        this.usdtPrice = '1'
      })
    })
  }

  @asyncAction *refreshData() {
    yield this.loadData()
    this.stakingSBPoolHandler?.getStakeApy()
    this.stakingSTEPoolHandler?.getStakeApy()
    this.stakingUSDTPoolHandler?.getStakeApy()
    this.fetchUserInfo()
  }

  @asyncAction *fetchUserInfo() {
    const [sbPoolUserInfo, stePoolUserInfo, usdtPoolUserInfo] = yield Promise.all([
      this.stakingSBPoolHandler?.getPoolUserInfo(this.sbStakingPool, walletStore.account),
      this.stakingSTEPoolHandler?.getPoolUserInfo(this.steStakingPool, walletStore.account),
      this.stakingUSDTPoolHandler?.getPoolUserInfo(this.usdtStakingPool, walletStore.account)
    ])

    // sb
    this.sbStakedAmount = bnHelper.fromDecimals(sbPoolUserInfo.stakedAmount, this.stakingSBPoolHandler?.tokenDecimals)
    this.sbPendingReward = bnHelper.fromDecimals(sbPoolUserInfo.pendingReward, this.stakingSBPoolHandler?.tokenDecimals)
    this.sbUnStakingAmount = sbPoolUserInfo.withdrawingAmount
    this.sbRequestWithdrawTime = sbPoolUserInfo.requestWithdrawTime

    // ste
    this.steStakedAmount = bnHelper.fromDecimals(
      stePoolUserInfo.stakedAmount,
      this.stakingSTEPoolHandler?.tokenDecimals
    )
    this.stePendingReward = bnHelper.fromDecimals(
      stePoolUserInfo.pendingReward,
      this.stakingSTEPoolHandler?.tokenDecimals
    )
    this.steUnStakingAmount = stePoolUserInfo.withdrawingAmount
    this.steRequestWithdrawTime = stePoolUserInfo.requestWithdrawTime

    // usdt
    this.usdtStakedAmount = bnHelper.fromDecimals(
      usdtPoolUserInfo.stakedAmount,
      this.stakingUSDTPoolHandler?.tokenDecimals
    )
    this.usdtPendingReward = bnHelper.fromDecimals(
      usdtPoolUserInfo.pendingReward,
      this.stakingUSDTPoolHandler?.tokenDecimals
    )
    this.usdtUnStakingAmount = usdtPoolUserInfo.withdrawingAmount
    this.usdtRequestWithdrawTime = usdtPoolUserInfo.requestWithdrawTime
  }

  //#region SB
  @action.bound changeSbStakeInputAmount(input) {
    this.sbStakeInputAmount = input
  }
  @action.bound setMaxInputSBStake() {
    this.sbStakeInputAmount = walletStore.sbBalance.toString()
  }
  @action.bound setOpenSBStakingDialog(value: boolean) {
    this.openSBStakingDialog = value
    if (!value) this.sbStakeInputAmount = ''
  }

  @observable sbClaimableTimeEstimate: any
  @action.bound setOpenSBUnStakeDialog(value: boolean) {
    this.openSBUnStakeDialog = value
    if (value) {
      const lockDuration = this.sbPoolInfo?.lockDuration
      const expectTime = moment().add(lockDuration)
      this.sbClaimableTimeEstimate = moment(expectTime, 'X').format('DD/MM/YYYY HH:mm')
    }
  }
  @action.bound setOpenSBHavestDialog(value: boolean) {
    this.openSBHavestDialog = value
  }

  @asyncAction *stakeSB() {
    try {
      this.stakeLoading = true
      if (this.sbStakeInputAmount) {
        yield this.stakingSBPoolHandler?.stake(this.sbStakingPool, this.sbStakeInputAmount, walletStore.account)
        this.refreshData()
        this.setOpenSBStakingDialog(false)
        this.sbStakeInputAmount = ''
        snackController.success('Stake successfully')
      }
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeLoading = false
    }
  }
  @asyncAction *unStakeSB() {
    try {
      this.stakeLoading = true
      yield this.stakingSBPoolHandler?.unStake(this.sbStakingPool, walletStore.account)
      this.setOpenSBUnStakeDialog(false)
      this.refreshData()
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeLoading = false
    }
  }
  @asyncAction *approveSB() {
    try {
      this.sbApproving = true
      yield this.stakingSBPoolHandler?.approve(walletStore.account)
      this.sbApproved = true
    } catch (error) {
      this.sbApproved = false
      snackController.error((error as any).message)
    } finally {
      this.sbApproving = false
    }
  }
  @asyncAction *withdrawSB() {
    try {
      this.stakeLoading = true
      yield this.stakingSBPoolHandler?.withdraw(this.sbStakingPool, walletStore.account)
      this.refreshData()
      snackController.success('Claim successfully')
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeLoading = false
    }
  }
  @asyncAction *havestSB() {
    try {
      this.stakeLoading = true
      yield this.stakingSBPoolHandler?.havest(this.sbStakingPool, walletStore.account)
      this.setOpenSBHavestDialog(false)
      this.refreshData()
      snackController.success('Harvest successfully')
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeLoading = false
    }
  }

  @computed get sbStaked() {
    return bnHelper.gt(this.sbStakedAmount, Zero)
  }

  @computed get sbTotalStakeInput() {
    try {
      const fixedInputAmount = this.sbStakeInputAmount ? FixedNumber.from(this.sbStakeInputAmount) : Zero
      if (this.sbStakedAmount) return this.sbStakedAmount.addUnsafe(fixedInputAmount)
      else return Zero
    } catch (error) {
      return Zero
    }
  }

  @computed get rewardEstimated() {
    return this.sbTotalStakeInput?.mulUnsafe(this.sbStakingAPR) || Zero
  }

  @computed get yourSBStakedValue() {
    return this.sbStakedAmount.mulUnsafe(FixedNumber.from(this.sbPrice))
  }

  @computed get sbPoolTotalStaked() {
    return this.sbPoolInfo?.amount
  }

  @computed get sbPoolTotalValue() {
    return this.sbPoolTotalStaked?.mulUnsafe(FixedNumber.from(this.sbPrice))
  }

  @computed get sbPoolUnStakeLockDuration() {
    return this.sbPoolInfo?.lockDuration.humanize()
  }

  @computed get sbHaveWithdrawableAmount() {
    return bnHelper.gt(this.sbUnStakingAmount, Zero)
  }

  @computed get sbRequestUnstakeTime() {
    return moment(this.sbRequestWithdrawTime, 'X').format('DD/MM/YYYY HH:mm')
  }
  @computed get sbWithdrawableTime() {
    const lockDuration = this.sbPoolInfo?.lockDuration
    const withdrawableTime = moment(this.sbRequestWithdrawTime, 'X').add(lockDuration)
    return moment(withdrawableTime, 'X').format('DD/MM/YYYY HH:mm')
  }

  @computed get sbWithdrawable() {
    const lockDuration = this.sbPoolInfo?.lockDuration
    const withdrawableTime = moment(this.sbRequestWithdrawTime, 'X').add(lockDuration)
    return moment().isAfter(withdrawableTime)
  }

  @computed get sbUnStakeEnable() {
    return bnHelper.gt(this.sbStakedAmount, Zero)
  }

  @computed get sbHavestEnable() {
    return bnHelper.gt(this.sbPendingReward, Zero)
  }

  @computed get validSbInput() {
    try {
      return bnHelper.lte(FixedNumber.from(this.sbStakeInputAmount), walletStore.sbBalance)
    } catch (error) {
      return false
    }
  }
  //#endregion

  //#region STE
  @action.bound changeSTEStakeInputAmount(input) {
    this.steStakeInputAmount = input
  }
  @action.bound setMaxInputSTEStake() {
    this.steStakeInputAmount = walletStore.steBalance.toString()
  }
  @action.bound setOpenSTEStakingDialog(value: boolean) {
    this.openSTEStakingDialog = value
    if (!value) this.steStakeInputAmount = ''
  }
  @observable steClaimableTimeEstimate: any
  @action.bound setOpenSTEUnStakeDialog(value: boolean) {
    this.openSTEUnStakeDialog = value
    if (value) {
      const lockDuration = this.stePoolInfo?.lockDuration
      const expectTime = moment().add(lockDuration)
      this.steClaimableTimeEstimate = moment(expectTime, 'X').format('DD/MM/YYYY HH:mm')
    }
  }
  @action.bound setOpenSTEHavestDialog(value: boolean) {
    this.openSTEHavestDialog = value
  }

  @asyncAction *stakeSTE() {
    try {
      this.stakeSTELoading = true
      if (this.steStakeInputAmount) {
        yield this.stakingSTEPoolHandler?.stake(this.steStakingPool, this.steStakeInputAmount, walletStore.account)
        this.refreshData()
        this.setOpenSTEStakingDialog(false)
        this.steStakeInputAmount = ''
        snackController.success('Stake successfully')
      }
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeSTELoading = false
    }
  }

  @asyncAction *unStakeSTE() {
    try {
      this.stakeSTELoading = true
      yield this.stakingSTEPoolHandler?.unStake(this.steStakingPool, walletStore.account)
      this.setOpenSTEUnStakeDialog(false)
      this.refreshData()
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeSTELoading = false
    }
  }

  @asyncAction *havestSTE() {
    try {
      this.stakeSTELoading = true
      yield this.stakingSTEPoolHandler?.havest(this.steStakingPool, walletStore.account)
      this.setOpenSTEHavestDialog(false)
      this.refreshData()
      snackController.success('Harvest successfully')
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeSTELoading = false
    }
  }

  @asyncAction *withdrawSTE() {
    try {
      this.stakeSTELoading = true
      yield this.stakingSTEPoolHandler?.withdraw(this.steStakingPool, walletStore.account)
      this.refreshData()
      snackController.success('Claim successfully')
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeSTELoading = false
    }
  }

  @asyncAction *approveSTE() {
    try {
      this.steApproving = true
      yield this.stakingSTEPoolHandler?.approve(walletStore.account)
      this.steApproved = true
    } catch (error) {
      this.steApproved = false
      snackController.error((error as any).message)
    } finally {
      this.steApproving = false
    }
  }

  @computed get steStaked() {
    return bnHelper.gt(this.steStakedAmount, Zero)
  }

  @computed get steTotalStakeInput() {
    try {
      const fixedInputAmount = this.steStakeInputAmount ? FixedNumber.from(this.steStakeInputAmount) : Zero
      if (this.steStakedAmount) return this.steStakedAmount.addUnsafe(fixedInputAmount)
      else return Zero
    } catch (error) {
      return Zero
    }
  }

  @computed get rewardEstimatedSTE() {
    return this.steTotalStakeInput?.mulUnsafe(this.steStakingAPR) || Zero
  }

  @computed get yourSTEStakedValue() {
    return this.steStakedAmount.mulUnsafe(FixedNumber.from(this.stePrice))
  }

  @computed get stePoolTotalStaked() {
    return this.stePoolInfo?.amount
  }

  @computed get stePoolTotalValue() {
    return this.stePoolTotalStaked?.mulUnsafe(FixedNumber.from(this.stePrice))
  }

  @computed get stePoolUnStakeLockDuration() {
    return this.stePoolInfo?.lockDuration.humanize()
  }

  @computed get steHaveWithdrawableAmount() {
    return bnHelper.gt(this.steUnStakingAmount, Zero)
  }

  @computed get steRequestUnstakeTime() {
    return moment(this.steRequestWithdrawTime, 'X').format('DD/MM/YYYY HH:mm')
  }

  @computed get steWithdrawableTime() {
    const lockDuration = this.stePoolInfo?.lockDuration
    const withdrawableTime = moment(this.steRequestWithdrawTime, 'X').add(lockDuration)
    return moment(withdrawableTime, 'X').format('DD/MM/YYYY HH:mm')
  }

  @computed get steWithdrawable() {
    const lockDuration = this.stePoolInfo?.lockDuration
    const withdrawableTime = moment(this.steRequestWithdrawTime, 'X').add(lockDuration)
    return moment().isAfter(withdrawableTime)
  }

  @computed get steUnStakeEnable() {
    return bnHelper.gt(this.steStakedAmount, Zero)
  }

  @computed get steUnHavestEnable() {
    return bnHelper.gt(this.stePendingReward, Zero)
  }

  @computed get steHavestEnable() {
    return bnHelper.gt(this.stePendingReward, Zero)
  }

  @computed get validSteInput() {
    try {
      return bnHelper.lte(FixedNumber.from(this.steStakeInputAmount), walletStore.steBalance)
    } catch (error) {
      return false
    }
  }
  //#endregion

  //#region USDT
  @action.bound changeUSDTStakeInputAmount(input) {
    this.usdtStakeInputAmount = input
  }
  @action.bound setMaxInputUSDTStake() {
    this.usdtStakeInputAmount = walletStore.usdtBalance.toString()
  }
  @action.bound setOpenUSDTStakingDialog(value: boolean) {
    this.openUSDTStakingDialog = value
    if (!value) this.usdtStakeInputAmount = ''
  }
  @observable usdtClaimableTimeEstimate: any
  @action.bound setOpenUSDTUnStakeDialog(value: boolean) {
    this.openUSDTUnStakeDialog = value
    if (value) {
      const lockDuration = this.usdtPoolInfo?.lockDuration
      const expectTime = moment().add(lockDuration)
      this.usdtClaimableTimeEstimate = moment(expectTime, 'X').format('DD/MM/YYYY HH:mm')
    }
  }
  @action.bound setOpenUSDTHavestDialog(value: boolean) {
    this.openUSDTHavestDialog = value
  }

  @asyncAction *stakeUSDT() {
    try {
      this.stakeUSDTLoading = true
      if (this.usdtStakeInputAmount) {
        yield this.stakingUSDTPoolHandler?.stake(this.usdtStakingPool, this.usdtStakeInputAmount, walletStore.account)
        this.refreshData()
        this.setOpenUSDTStakingDialog(false)
        this.usdtStakeInputAmount = ''
        snackController.success('Stake successfully')
      }
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeUSDTLoading = false
    }
  }

  @asyncAction *unStakeUSDT() {
    try {
      this.stakeUSDTLoading = true
      yield this.stakingUSDTPoolHandler?.unStake(this.usdtStakingPool, walletStore.account)
      this.setOpenUSDTUnStakeDialog(false)
      this.refreshData()
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeUSDTLoading = false
    }
  }

  @asyncAction *havestUSDT() {
    try {
      this.stakeUSDTLoading = true
      yield this.stakingUSDTPoolHandler?.havest(this.usdtStakingPool, walletStore.account)
      this.setOpenUSDTHavestDialog(false)
      this.refreshData()
      snackController.success('Harvest successfully')
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeUSDTLoading = false
    }
  }

  @asyncAction *withdrawUSDT() {
    try {
      this.stakeUSDTLoading = true
      yield this.stakingUSDTPoolHandler?.withdraw(this.usdtStakingPool, walletStore.account)
      this.refreshData()
      snackController.success('Claim successfully')
    } catch (error) {
      snackController.commonError(error)
    } finally {
      this.stakeUSDTLoading = false
    }
  }

  @asyncAction *approveUSDT() {
    try {
      this.usdtApproving = true
      yield this.stakingUSDTPoolHandler?.approve(walletStore.account)
      this.usdtApproved = true
    } catch (error) {
      this.usdtApproved = false
      snackController.error((error as any).message)
    } finally {
      this.usdtApproving = false
    }
  }

  @computed get usdtStaked() {
    return bnHelper.gt(this.usdtStakedAmount, Zero)
  }

  @computed get usdtTotalStakeInput() {
    try {
      const fixedInputAmount = this.usdtStakeInputAmount ? FixedNumber.from(this.usdtStakeInputAmount) : Zero
      if (this.usdtStakedAmount) return this.usdtStakedAmount.addUnsafe(fixedInputAmount)
      else return Zero
    } catch (error) {
      return Zero
    }
  }

  @computed get rewardEstimatedUSDT() {
    return this.usdtTotalStakeInput?.mulUnsafe(this.usdtStakingAPR) || Zero
  }

  @computed get yourUSDTStakedValue() {
    return this.usdtStakedAmount.mulUnsafe(FixedNumber.from(this.usdtPrice))
  }

  @computed get usdtPoolTotalStaked() {
    return this.usdtPoolInfo?.amount
  }

  @computed get usdtPoolTotalValue() {
    return this.usdtPoolTotalStaked?.mulUnsafe(FixedNumber.from(this.usdtPrice))
  }

  @computed get usdtPoolUnStakeLockDuration() {
    return this.usdtPoolInfo?.lockDuration.humanize()
  }

  @computed get usdtHaveWithdrawableAmount() {
    return bnHelper.gt(this.usdtUnStakingAmount, Zero)
  }

  @computed get usdtRequestUnstakeTime() {
    return moment(this.usdtRequestWithdrawTime, 'X').format('DD/MM/YYYY HH:mm')
  }

  @computed get usdtWithdrawableTime() {
    const lockDuration = this.usdtPoolInfo?.lockDuration
    const withdrawableTime = moment(this.usdtRequestWithdrawTime, 'X').add(lockDuration)
    return moment(withdrawableTime, 'X').format('DD/MM/YYYY HH:mm')
  }

  @computed get usdtWithdrawable() {
    const lockDuration = this.usdtPoolInfo?.lockDuration
    const withdrawableTime = moment(this.usdtRequestWithdrawTime, 'X').add(lockDuration)
    return moment().isAfter(withdrawableTime)
  }

  @computed get usdtUnStakeEnable() {
    return bnHelper.gt(this.usdtStakedAmount, Zero)
  }

  @computed get usdtUnHavestEnable() {
    return bnHelper.gt(this.usdtPendingReward, Zero)
  }

  @computed get usdtHavestEnable() {
    return bnHelper.gt(this.usdtPendingReward, Zero)
  }

  @computed get validUsdInput() {
    try {
      return bnHelper.lte(FixedNumber.from(this.usdtStakeInputAmount), walletStore.usdtBalance)
    } catch (error) {
      return false
    }
  }
  //#endregion
}
